"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";

import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";
import { getOperationByKey } from "@/lib/api/suppliers";
import { supplierStateLabel } from "@/lib/suppliers/state-labels";
import { requestEventKey } from "@/lib/commands/request-messages";
import {
  completedRequestId,
  completedSupplierId,
  completionPath,
} from "@/lib/commands/request-completion";
import { useWorkspaceContext } from "./workspace-context";

export function WorkspaceOperationNotices() {
  const t = useTranslations("suppliers.operationNotices");
  const labels = useTranslations("suppliers.labels");
  const requests = useTranslations("requests");
  const sourcing = useTranslations("sourcing");
  const workspace = useWorkspaceContext();
  const router = useRouter();
  const pathname = usePathname();
  const controller = useRef<AbortController | null>(null);
  const [lookupKey, setLookupKey] = useState<string>();
  const [failedLookupKey, setFailedLookupKey] = useState<string>();
  useEffect(() => () => controller.current?.abort(), []);
  useEffect(() => {
    const completed = workspace.requestCompletion;
    if (
      completed &&
      pathname === completionPath(completed) &&
      workspace.operations.some((item) => item.idempotencyKey === completed.key)
    )
      workspace.clearOperation(completed.key);
  }, [pathname, workspace]);

  async function lookup(operationType: string, idempotencyKey: string) {
    if (!workspace.context || controller.current) return;
    const epoch = workspace.getAuthorityEpoch();
    controller.current = new AbortController();
    setLookupKey(idempotencyKey);
    setFailedLookupKey(undefined);
    try {
      const response = await getOperationByKey(
        {
          tenantId: workspace.context.tenantId,
          signal: controller.current.signal,
        },
        operationType,
        idempotencyKey,
      );
      if (
        controller.current.signal.aborted ||
        !workspace.isAuthorityCurrent(epoch)
      )
        return;
      if (
        (operationType.startsWith("request.") ||
          operationType.startsWith("sourcing.") ||
          operationType.startsWith("supplier.")) &&
        response.data.state.toLowerCase() === "completed"
      ) {
        const id = operationType.startsWith("supplier.")
          ? completedSupplierId(response.data.result)
          : completedRequestId(response.data.result);
        if (!id) throw new Error("Missing authoritative request identity");
        workspace.completeRequest({ id, key: idempotencyKey, operationType });
        workspace.resolveOperation(idempotencyKey, response.data.state);
        router.replace(
          completionPath({ id, key: idempotencyKey, operationType }),
        );
      } else workspace.resolveOperation(idempotencyKey, response.data.state);
    } catch (error) {
      if (!workspace.isAuthorityCurrent(epoch)) return;
      if (
        error instanceof ApiError &&
        (error.kind === "unauthorized" ||
          error.kind === "forbidden" ||
          (error.kind === "not-found" && error.code !== "operation_not_found"))
      ) {
        controller.current?.abort();
        workspace.refreshAuthority(error);
      } else if (!(error instanceof ApiError && error.kind === "cancelled"))
        setFailedLookupKey(idempotencyKey);
    } finally {
      controller.current = null;
      setLookupKey(undefined);
    }
  }

  const notices = workspace.operations.filter(
    (item) => item.status !== "pending",
  );
  if (!notices.length) return null;
  return (
    <aside className="container-fluid pt-4" aria-label={t("regionLabel")}>
      {notices.map((notice) => (
        <div
          key={notice.idempotencyKey}
          role="alert"
          className="rounded-md border border-warning-border bg-warning-soft p-3 text-sm text-warning-foreground"
        >
          <p>{t(notice.status)}</p>
          <p className="mt-1 font-medium">
            {notice.operationType.startsWith("request.")
              ? requests(requestEventKey(notice.operationType.slice(8)))
              : notice.operationType.startsWith("sourcing.")
                ? sourcing("operation")
                : labels(supplierStateLabel(notice.operationType))}
          </p>
          {notice.resultState ? (
            <p className="mt-1" role="status">
              {t("result", {
                state: labels(supplierStateLabel(notice.resultState)),
              })}
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            {notice.status === "unknown" ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={Boolean(lookupKey)}
                onClick={() =>
                  void lookup(notice.operationType, notice.idempotencyKey)
                }
              >
                {lookupKey === notice.idempotencyKey
                  ? t("lookingUp")
                  : t("lookup")}
              </Button>
            ) : null}
            {notice.status === "resolved" &&
            workspace.requestCompletion?.key !== notice.idempotencyKey ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => workspace.clearOperation(notice.idempotencyKey)}
              >
                {t("dismiss")}
              </Button>
            ) : null}
          </div>
          {failedLookupKey === notice.idempotencyKey ? (
            <p role="alert" className="mt-2 text-validation-foreground">
              {t("lookupFailed")}
            </p>
          ) : null}
        </div>
      ))}
    </aside>
  );
}
